/**
 * Low-level writes: apply to the local mirror, then queue for the server in the outbox.
 * Repeated edits to the same row are merged while they wait, so an offline session that
 * renames a task five times sends one update.
 */
import { db, rowKey, type OutboxEntry, type SyncTable } from '@/db/dexie';
import { isLive } from '@/state/session';

type Row = Record<string, unknown>;

const outboxListeners = new Set<() => void>();
export function onOutboxChange(cb: () => void): () => void {
  outboxListeners.add(cb);
  return () => outboxListeners.delete(cb);
}
function notifyOutbox(): void {
  outboxListeners.forEach((cb) => cb());
}

export function nowIso(): string {
  return new Date().toISOString();
}

async function enqueue(
  table: SyncTable,
  key: string,
  op: OutboxEntry['op'],
  payload: Row,
  baseUpdatedAt: string | null,
): Promise<void> {
  if (!isLive()) return;
  const d = db();
  const pending = await d.outbox
    .where('[table+key]')
    .equals([table, key])
    .filter((e) => e.inflight === 0)
    .last();
  if (pending?.seq !== undefined) {
    const merged: Row = { ...pending.payload, ...payload };
    await d.outbox.update(pending.seq, {
      op: pending.op === 'upsert' || op === 'upsert' ? 'upsert' : 'patch',
      payload: merged,
    });
    return;
  }
  await d.outbox.add({
    table,
    key,
    op,
    payload,
    base_updated_at: baseUpdatedAt,
    created_at: nowIso(),
    attempts: 0,
    inflight: 0,
    last_error: null,
  });
}

/** Insert or replace whole rows. */
export async function putRows(table: SyncTable, rows: Row[]): Promise<void> {
  if (rows.length === 0) return;
  const d = db();
  await d.transaction('rw', [d.table_(table), d.outbox], async () => {
    await d.table_(table).bulkPut(rows);
    for (const row of rows) await enqueue(table, rowKey(table, row), 'upsert', row, null);
  });
  notifyOutbox();
}

/** Change some fields of an existing row. Returns the updated row. */
export async function patchRow<T extends object>(
  table: SyncTable,
  key: string | [string, string],
  patch: Partial<T>,
): Promise<T | undefined> {
  const d = db();
  let result: T | undefined;
  await d.transaction('rw', [d.table_(table), d.outbox], async () => {
    const existing = (await d.table_(table).get(key)) as T | undefined;
    if (!existing) return;
    const next = { ...existing, ...patch, updated_at: nowIso() } as T;
    await d.table_(table).put(next as Row);
    const k = Array.isArray(key) ? key.join(':') : key;
    const serverPatch: Row = { ...patch } as Row;
    if (table === 'task_tags') {
      // Composite-key rows are always sent whole.
      await enqueue(
        table,
        k,
        'upsert',
        next as Row,
        ((existing as Row).updated_at as string) ?? null,
      );
    } else {
      await enqueue(
        table,
        k,
        'patch',
        serverPatch,
        ((existing as Row).updated_at as string) ?? null,
      );
    }
    result = next;
  });
  notifyOutbox();
  return result;
}

/** Patch many rows of one table in one transaction. */
export async function patchRows<T extends object>(
  table: SyncTable,
  patches: { key: string; patch: Partial<T> }[],
): Promise<void> {
  if (patches.length === 0) return;
  const d = db();
  await d.transaction('rw', [d.table_(table), d.outbox], async () => {
    for (const { key, patch } of patches) {
      const existing = (await d.table_(table).get(key)) as T | undefined;
      if (!existing) continue;
      await d.table_(table).put({ ...existing, ...patch, updated_at: nowIso() } as Row);
      await enqueue(
        table,
        key,
        'patch',
        { ...patch } as Row,
        ((existing as Row).updated_at as string) ?? null,
      );
    }
  });
  notifyOutbox();
}

/** Soft delete (moves to Trash; purged after 30 days on the server). */
export async function softDelete(table: SyncTable, keys: string[]): Promise<void> {
  const deleted_at = nowIso();
  await patchRows(
    table,
    keys.map((key) => ({ key, patch: { deleted_at } })),
  );
}

export async function restore(table: SyncTable, keys: string[]): Promise<void> {
  await patchRows(
    table,
    keys.map((key) => ({ key, patch: { deleted_at: null } })),
  );
}

export async function pendingKeys(): Promise<Set<string>> {
  const rows = await db().outbox.toArray();
  return new Set(rows.map((r) => `${r.table}:${r.key}`));
}
